import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  message,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Spin,
  Form,
  Empty,
} from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { logAPIWithErrorHandling } from "../../../api/logs/log";

const { Title, Text } = Typography;

const LogScreen = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logText, setLogText] = useState("");

  const fetchDailyLog = async (date) => {
    try {
      setLoading(true);
      const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
      const params = {
        date: formattedDate
      };

      const response = await logAPIWithErrorHandling.getLogs(params);
      // Axios tự động parse JSON, không cần .json()
      const result = response.data;
      if (result.success && result.data && Array.isArray(result.data)) {
        setLogData(result.data);
        
        // Logs đã ở dạng string format sẵn, chỉ cần join với newline
        const textLogs = result.data.join('\n');
        
        setLogText(textLogs);
        message.success("Tải nhật ký thành công!");
      } else {
        setLogData([]);
        setLogText("");
        message.warning("Không có dữ liệu nhật ký cho ngày đã chọn!");
      }
    } catch (error) {
      console.error("Error fetching daily log:", error);
      message.error(error.message || "Không thể tải lịch sử");
      setLogData([]);
      setLogText("");
    } finally {
      setLoading(false);
    }
  };

  // Load today's log on first render
  useEffect(() => {
    fetchDailyLog(dayjs());
    form.setFieldsValue({ logDate: dayjs() });
  }, []);

  const handleSearchLog = async (values) => {
    const { logDate } = values;
    await fetchDailyLog(logDate);
  };

  const handleRefresh = () => {
    const currentDate = form.getFieldValue('logDate') || dayjs();
    fetchDailyLog(currentDate);
  };

  const handleCopyLog = () => {
    if (logText) {
      navigator.clipboard.writeText(logText).then(() => {
        message.success("Đã sao chép log vào clipboard!");
      }).catch(() => {
        message.error("Không thể sao chép log!");
      });
    }
  };

  return (
    <Card
      className="log-screen-card"
      style={{
        width: "100%",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0, color: "#0070D7" }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Nhật Ký Hệ Thống
          </Title>
          <Text type="secondary">
            Xem và theo dõi nhật ký hoạt động của hệ thống
          </Text>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearchLog}
          initialValues={{ logDate: dayjs() }}
        >
          <Row gutter={24} align="bottom" style={{ display: "flex", alignItems: "center"}}>
            <Col xs={24} md={16}>
              <Form.Item
                name="logDate"
                label={
                  <Space>
                    <CalendarOutlined />
                    <span>Chọn ngày xem log</span>
                  </Space>
                }
                rules={[{ required: true, message: "Vui lòng chọn ngày!" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SearchOutlined />}
                >
                  Xem log
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                >
                  Làm mới
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyLog}
                  disabled={!logText}
                >
                  Sao chép
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <Divider style={{ margin: "16px 0" }} />

        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: 16 }}>
              Nhật ký hệ thống {form.getFieldValue('logDate')?.format('DD/MM/YYYY')}
            </Text>
            <Text type="secondary">
              Tổng: {logData.length} bản ghi
            </Text>
          </div>

          <Spin spinning={loading} tip="Đang tải nhật ký...">
            <div
              style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #CBD5E1',
                borderRadius: 6,
                padding: 16,
                minHeight: 400,
                maxHeight: 600,
                overflowY: 'auto',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: 13,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {logText ? (
                <Text style={{ color: '#333' }}>
                  {logText}
                </Text>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu nhật ký"
                  style={{ margin: '60px 0' }}
                />
              )}
            </div>
          </Spin>
        </div>
      </Space>
    </Card>
  );
};

export default LogScreen;